import React, { useState, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { FileDetailModal, MoFile } from '../components/FileDetailModal';
// 변환 진행률 푸시(WS transcode_progress/complete/failed → App.tsx → 스토어). 카드 배지를
// 진행률로 바꾸고, 완료 푸시가 오면 목록을 다시 읽는다 — Basic.tsx 와 같은 방식.
import { useFileTranscodeStore, useTranscodeEntry } from '../stores/fileTranscodeStore';
import { transcodeBadge } from '../utils/transcodeBadge';
import RecipeInterview, { RecipeValues, toRecipeBody } from '../components/RecipeInterview';
import AiVideoModal from '../components/AiVideoModal';
import UploadCalendar, { CalEvent } from '../components/UploadCalendar';
import { formatDate, isExpired, eventDDay } from '../utils/datetime';
import { eventDateFromFilename } from '../utils/filenameDate';
// 행사 시작·시간·종료 양방향 계산 — 영상 상세 모달·비로그인 업로드와 **같은 규칙**.
import {
  DEFAULT_DURATION_MIN, durationFromEnd, endFromDuration, humanizeMinutes, isRangeInvalid,
  onStartChanged, snapOnStartBlur,
} from '../utils/eventDuration';

/** datetime-local 입력값 + N시간 → datetime-local 입력값(빈값이면 빈값). 만료 자동채움용. */
const localInputPlusHours = (localInput: string, hours: number): string => {
 if (!localInput) return '';
 const t = new Date(localInput).getTime();
 if (!Number.isFinite(t)) return '';
 const d = new Date(t + hours * 3600_000);
 const p = (n: number) => String(n).padStart(2, '0');
 return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

interface MoFileStore {
 files: MoFile[];
 isLoading: boolean;
 error: string | null;
 currentFilter: 'all' | 'video' | 'image';

 setFiles: (files: MoFile[]) => void;
 setLoading: (loading: boolean) => void;
 setError: (error: string | null) => void;
 setFilter: (filter: 'all' | 'video' | 'image') => void;

 deleteFile: (fileHash: string) => void;
 updateFileTitle: (fileHash: string, newTitle: string) => void;
}

const useMoFileStore = create<MoFileStore>((set) => ({
 files: [],
 isLoading: false,
 error: null,
 currentFilter: 'all',

 setFiles: (files) => set({ files }),
 setLoading: (loading) => set({ isLoading: loading }),
 setError: (error) => set({ error }),
 setFilter: (filter) => set({ currentFilter: filter }),

 deleteFile: (fileHash) => set((state) => ({
 files: state.files.filter(f => f.file_hash !== fileHash)
 })),
 updateFileTitle: (fileHash, newTitle) => set((state) => ({
 files: state.files.map(f => f.file_hash === fileHash ? { ...f, title: newTitle } : f)
 }))
}));

// Components
const FileCard: React.FC<{ file: MoFile; onClick: () => void; selected: boolean; onToggleSelect: () => void }> = ({ file, onClick, selected, onToggleSelect }) => {
 const isVideo = file.file_type?.startsWith('video/');
 const isImage = file.file_type?.startsWith('image/');
 const thumbnailUrl = file.thumbnail_path ? `/mofile/thumbnail/${file.thumbnail_path}` : null;
 const transcodeStatus = file.transcode_status ?? 'done';
 // 대표 영상의 실시간 변환 상태(WS 푸시). 목록 fetch 값보다 최신이라 진행률 표시에 쓴다.
 const wsTranscode = useTranscodeEntry(file.file_hash, null);
 // 공용 배지 유틸 — 서버(목록) 상태 + WS 스토어. preparing = 사진 준비 단계(encoding_started_at 없음).
 const prepBadge = transcodeBadge({
  server: {
   status: transcodeStatus,
   progress: file.transcode_progress,
   preparing: transcodeStatus === 'processing' && !(file as any).encoding_started_at,
   error: file.transcode_error,
  },
  entry: wsTranscode,
 });
 // 라이프타임 만료 영상: playing-info 가 슬롯에서 제외(재생 안 됨) → 목록에서 dimmed 표시.
 const expired = isExpired(file.expires_at);
 // 만료 아니고 행사가 미래면 D-N(남은 일수) 뱃지.
 const dday = expired ? null : eventDDay(file.event_start_at);

 const formatFileSize = (bytes: number | undefined) => {
 if (!bytes) return '0 Bytes';
 const k = 1024;
 const sizes = ['Bytes', 'KB', 'MB', 'GB'];
 const i = Math.floor(Math.log(bytes) / Math.log(k));
 return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
 };

 const statusBadge = (() => {
  if (!isVideo) return null;
  if (transcodeStatus === 'processing' || transcodeStatus === 'pending') {
   return (
    <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 bg-opacity-90 text-white text-xs font-medium rounded flex items-center gap-1">
     <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
     </svg>
     {prepBadge.text}
    </div>
   );
  }
  if (transcodeStatus === 'failed') {
   return (
    <div className="absolute top-2 left-2 px-2 py-1 bg-red-500 bg-opacity-90 text-white text-xs font-medium rounded">
     게재 준비 실패
    </div>
   );
  }
  return null;
 })();

 // 달력 드롭 편성(퀵UI) 드래그 소스 — 트랜스코딩 완료 영상만. 만료 파일은 허용(재게시).
 const draggable = isVideo && transcodeStatus === 'done';
 return (
 <div
 className={`border overflow-hidden hover:shadow-md transition-shadow cursor-pointer${expired ? ' opacity-50' : ''}${selected ? ' ring-2 ring-blue-500 border-blue-500' : ' border-gray-200'}`}
 onClick={onClick}
 title={expired ? '만료됨 — 재생 안 됨 (달력에 끌어다 놓으면 다시 송출)' : (draggable ? '달력 날짜에 끌어다 놓으면 송출 조건 창이 열립니다' : undefined)}
 draggable={draggable}
 onDragStart={(e) => {
  if (!draggable) return;
  e.dataTransfer.setData('application/x-video-drag', JSON.stringify({ fileHash: file.file_hash, title: file.title || file.file_hash }));
  e.dataTransfer.effectAllowed = 'copy';
  (e.currentTarget as HTMLElement).style.opacity = '0.3';
 }}
 onDragEnd={(e) => { (e.currentTarget as HTMLElement).style.opacity = ''; }}
 >
 <div className="aspect-video bg-gray-100 relative">
 {/* 선택 체크박스 — 카드 클릭(상세 모달)과 분리(stopPropagation). <label> 은 input 활성화로
     onClick 이 이중 발화(토글 2회=제자리)되므로 <div> 사용. input 은 pointer-events-none 순수 표시. */}
 <div
 className="absolute top-2 left-2 z-20 flex items-center justify-center w-6 h-6 bg-white bg-opacity-80 rounded cursor-pointer"
 onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
 title="선택"
 >
 <input type="checkbox" checked={selected} readOnly tabIndex={-1} className="w-4 h-4 accent-blue-500 pointer-events-none" />
 </div>
 {expired ? (
 <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-gray-700 bg-opacity-90 text-white text-xs font-medium rounded">만료</div>
 ) : dday != null ? (
 <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-blue-600 bg-opacity-90 text-white text-xs font-medium rounded" title="행사 시작까지 남은 일수">D-{dday}</div>
 ) : null}
 {isImage ? (
 <img src={file.file_url} alt={file.title} className="w-full h-full object-contain" loading="lazy" />
 ) : isVideo ? (
 <>
 {thumbnailUrl ? (
 <img src={thumbnailUrl} alt={`${file.title} 썸네일`} className="w-full h-full object-contain" loading="lazy" />
 ) : (
 <div className="w-full h-full bg-gray-200 flex items-center justify-center">
 <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
 <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
 </svg>
 </div>
 )}
 </>
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
 d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
 </svg>
 </div>
 )}
 {statusBadge}
 </div>
 <div className="p-4">
 <h3 className="font-medium text-gray-900 truncate">{file.title}</h3>
 <p className="text-sm text-gray-500 mt-1">{formatFileSize(file.file_size)}</p>
 <p className="text-xs text-gray-400 mt-1">
 {formatDate(file.created_at)}
 </p>
 </div>
 </div>
 );
};

// Upload Modal Component
const UploadModal: React.FC<{ isOpen: boolean; onClose: () => void; onSuccess: () => void }> = ({
 isOpen,
 onClose,
 onSuccess
}) => {
 const [uploading, setUploading] = useState(false);
 const [progress, setProgress] = useState(0);
 const [title, setTitle] = useState('');
 const [fileError, setFileError] = useState<string | null>(null);

 // 행사 시각·동적 라벨: 연속 등록 편의를 위해 직전 값을 localStorage 에 기억해 기본값으로 채운다.
 // (controlled 라 form.reset() 에도 유지 — 다음 업로드 때 재입력 불필요.)
 const [eventStart, setEventStart] = useState(() => localStorage.getItem('moc:lastEventStart') || '');
 const [eventLabel, setEventLabel] = useState(() => localStorage.getItem('moc:lastEventLabel') || '');
 // 라이프타임 만료: 행사 시작 입력·수정 시 자동 체크 + start+1h(확정 규칙). 수동수정·해제 가능.
 const [eventExpires, setEventExpires] = useState('');
 const [expiresEnabled, setExpiresEnabled] = useState(false);
 // 행사 시간(분, 문자열) — 저장 필드가 아니라 시작·종료를 물리는 입력 보조. utils/eventDuration.
 const [durationMin, setDurationMin] = useState('');
 // 행사 시작 값 변경 시마다 만료 재계산(FileDetailModal 과 동일 규칙).
 const onChangeEventStart = (v: string) => {
  const r = onStartChanged(v, durationMin);
  setEventStart(r.start);
  setEventExpires(r.end);
  setDurationMin(r.durationMin);
  setExpiresEnabled(!!r.start);
 };

 // 행사 시간 입력 → 종료 계산. 시작이 없으면 값만 들고 있는다.
 const onChangeDuration = (v: string) => {
  setDurationMin(v);
  const end = endFromDuration(eventStart, v);
  if (end !== null) { setExpiresEnabled(true); setEventExpires(end); }
 };

 // 종료 입력 → 행사 시간 역산. 음수는 고치지 않고 경고만(입력이 튀지 않게).
 const onChangeExpires = (v: string) => {
  setEventExpires(v);
  setDurationMin(durationFromEnd(eventStart, v));
 };

 // 시작에서 포커스가 빠질 때 종료가 이르면 기본 행사 시간으로 스냅.
 const onBlurEventStart = () => {
  const snap = snapOnStartBlur(eventStart, eventExpires);
  if (snap) { setEventExpires(snap.end); setDurationMin(snap.durationMin); }
 };

 /** 종료가 시작보다 이르면 업로드 차단. */
 const durationInvalid = expiresEnabled && isRangeInvalid(eventStart, eventExpires);

 // Variant 자동 추천 + 사용자 선택
 // width/height 는 **패널 스캔아웃 치수**(회전 전)이고 그것이 variant 정체성이다
 // (variant_key·variant_path·PK 가 이 값에 물려 있으므로 절대 바꾸지 않는다).
 // rotation 은 별개 축이며, 사람이 보는 치수는 90/270 일 때 뒤바뀐다.
 interface VariantSpec { width: number; height: number; fps: number | null; rotation?: number; stretch: boolean; selected: boolean; clientCount?: number; clientNames?: string[]; isCustom?: boolean }

 /** 시청자가 실제로 보는 치수 — 세로 설치(90/270)면 가로·세로가 뒤바뀐다.
  *  패널이 3840x2160 이어도 세로로 걸면 콘텐츠는 2160 폭 × 3840 높이다. */
 const viewerDims = (v: { width: number; height: number; rotation?: number }): { w: number; h: number } =>
   (v.rotation === 90 || v.rotation === 270) ? { w: v.height, h: v.width } : { w: v.width, h: v.height };
 const [variants, setVariants] = useState<VariantSpec[]>([]);
 const [customW, setCustomW] = useState('');
 const [customH, setCustomH] = useState('');
 const [customFps, setCustomFps] = useState('');
 const [customStretch, setCustomStretch] = useState(false);
 // 이미지 → 영상 변환 옵션
 const [selectedIsImage, setSelectedIsImage] = useState(false);
 const [imgDurationSecs, setImgDurationSecs] = useState('10');
 const [imgWidth, setImgWidth] = useState('');

 useEffect(() => {
  if (!isOpen) return;
  // 모달 열릴 때마다 추천 갱신
  fetch('/mofile/recommended-variants', { credentials: 'include' })
    .then(r => r.ok ? r.json() : null)
    .then(body => {
      if (body?.success && Array.isArray(body.data?.variants)) {
        setVariants(body.data.variants.map((v: any) => ({
          width: v.width,
          height: v.height,
          fps: v.fps,
          rotation: v.rotation,
          stretch: false,
          selected: false,       // 기본 체크 해제 — 필요한 해상도만 사용자가 선택
          clientCount: v.clientCount,
          clientNames: v.clientNames,
          isCustom: false,
        })));
      }
    })
    .catch(() => { /* 무시 — variant는 선택사항 */ });
 }, [isOpen]);

 const addCustomVariant = () => {
   const w = parseInt(customW, 10);
   const h = parseInt(customH, 10);
   const fps = parseFloat(customFps);
   if (!Number.isInteger(w) || w <= 0 || !Number.isInteger(h) || h <= 0 || !Number.isFinite(fps) || fps <= 0) return;
   setVariants(prev => [...prev, { width: w, height: h, fps, stretch: customStretch, selected: true, isCustom: true }]);
   setCustomW(''); setCustomH(''); setCustomFps(''); setCustomStretch(false);
 };

 const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 const file = e.target.files?.[0];
 if (file) {
  setFileError(null);
  // 파일명에서 확장자 제거
  const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
  setTitle(nameWithoutExt);
  // 파일명 날짜("20260909"·"20260827-1900") → 행사 시작 프리필 — 이 파일에 특정된
  // 정보라 '마지막 값 기억' 프리필보다 우선한다. 기존 연동 핸들러로 넣어 만료도 물린다.
  const fnDate = eventDateFromFilename(file.name);
  if (fnDate) onChangeEventStart(fnDate);
  // 이미지 감지 → 영상 변환 입력 노출
  const isImage = file.type.startsWith('image/') ||
    /\.(jpe?g|png|webp|bmp|gif)$/i.test(file.name);
  setSelectedIsImage(isImage);
  if (isImage) {
   // 선택한 이미지의 실제 너비를 기본값으로 채운다(in-memory 디코드).
   const url = URL.createObjectURL(file);
   const img = new Image();
   img.onload = () => {
     URL.revokeObjectURL(url);
     if (img.naturalWidth > 0) setImgWidth(String(img.naturalWidth));
   };
   img.onerror = () => {
     URL.revokeObjectURL(url);
     // 디코드 실패 시에만 추천 너비 중 최대(없으면 1920)로 폴백, 기존 입력은 보존.
     // 회전 반영 후 폭을 쓴다 — 추천 칩과 같은 기준이어야 한다.
     setImgWidth(prev => {
       if (prev) return prev;
       const recWidths = variants
         .filter(v => !v.isCustom)
         .map(v => viewerDims(v).w)
         .filter(w => Number.isFinite(w) && w > 0);
       return String(recWidths.length > 0 ? Math.max(...recWidths) : 1920);
     });
   };
   img.src = url;
  }
 } else {
  setSelectedIsImage(false);
 }
 };

 const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
 e.preventDefault();
 const form = e.currentTarget;
 const formData = new FormData(form);

 // 행사 시작 시각: datetime-local(브라우저 로컬) → ISO instant 변환(타임존 정확). 빈값은 제거.
 const evRaw = formData.get('event_start_at');
 if (typeof evRaw === 'string' && evRaw) formData.set('event_start_at', new Date(evRaw).toISOString());
 else formData.delete('event_start_at');

 // 라이프타임 만료: 체크됐고 값 있으면 ISO 로 전송, 아니면 미전송.
 if (expiresEnabled && eventExpires) formData.set('expires_at', new Date(eventExpires).toISOString());
 else formData.delete('expires_at');

 // 동적 라벨: 빈값이면 제거.
 const lblRaw = formData.get('event_label');
 if (!(typeof lblRaw === 'string' && lblRaw.trim())) formData.delete('event_label');

 // 이미지 → 영상 변환 파라미터
 if (selectedIsImage) {
   const dur = parseFloat(imgDurationSecs);
   const w = parseInt(imgWidth, 10);
   if (!Number.isFinite(dur) || dur <= 0 || !Number.isInteger(w) || w <= 0) {
     alert('사진은 보여줄 시간(초)과 너비(px)를 적어 주세요.');
     return;
   }
   formData.append('image_duration_secs', String(dur));
   formData.append('image_width', String(w));
 }

 // Variant spec 직렬화
 const selectedVariants = variants.filter(v => v.selected && v.fps !== null).map(v => ({
   width: v.width, height: v.height, fps: v.fps, stretch: v.stretch
 }));
 if (selectedVariants.length > 0) {
   formData.append('variants', JSON.stringify(selectedVariants));
 }

 setUploading(true);
 setProgress(0);

 try {
 const xhr = new XMLHttpRequest();

 xhr.upload.onprogress = (event) => {
 if (event.lengthComputable) {
 const percentComplete = (event.loaded / event.total) * 100;
 setProgress(percentComplete);
 }
 };

 xhr.onload = () => {
 if (xhr.status === 200) {
 // 행사 시각·라벨은 다음 연속 등록을 위해 기억(유지). 나머지 폼은 초기화.
 localStorage.setItem('moc:lastEventStart', eventStart);
 localStorage.setItem('moc:lastEventLabel', eventLabel);
 form.reset();
 setTitle('');
 onSuccess();
 onClose();
 } else {
 let msg = '올리지 못했습니다.';
 try { msg = JSON.parse(xhr.responseText)?.error || msg; } catch { /* ignore */ }
 alert(msg);
 }
 setUploading(false);
 };

 xhr.onerror = () => {
 alert('업로드 중 오류가 발생했습니다.');
 setUploading(false);
 };

 xhr.open('POST', '/mofile/upload');
 xhr.withCredentials = true;
 xhr.send(formData);
 } catch (error) {
 console.error('Upload error:', error);
 alert('업로드 중 오류가 발생했습니다.');
 setUploading(false);
 }
 };

 if (!isOpen) return null;

 return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white p-6 max-w-md w-full min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden">
 <h2 className="text-xl font-bold mb-4">영상·이미지 업로드</h2>
 <form onSubmit={handleSubmit}>
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">파일 선택</label>
 <input
 type="file"
 name="file"
 accept="video/*,image/*"
 required
 disabled={uploading}
 onChange={handleFileChange}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 <p className="text-xs text-gray-500 mt-1">동영상 또는 이미지 파일을 선택하세요 (최대 3GB)</p>
 {fileError && (
  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
   <span>⚠</span> {fileError}
  </p>
 )}
 </div>

 {selectedIsImage && (
  <div className="mb-4 p-3 bg-blue-50 border border-blue-200">
   <p className="text-xs text-blue-900 mb-2">
    사진은 정한 시간만큼 화면에 보입니다 (높이는 비율대로 자동).
   </p>
   <div className="flex gap-2 mb-2">
    <div className="flex-1">
     <label className="block text-xs font-medium text-gray-700 mb-1">영상 길이 (초)</label>
     <input
      type="number"
      min="1"
      max="600"
      step="1"
      value={imgDurationSecs}
      onChange={(e) => setImgDurationSecs(e.target.value)}
      disabled={uploading}
      className="w-full px-2 py-1 border border-gray-300 text-sm"
     />
    </div>
    <div className="flex-1">
     <label className="block text-xs font-medium text-gray-700 mb-1">너비 (px)</label>
     <input
      type="number"
      min="100"
      max="7680"
      step="1"
      value={imgWidth}
      onChange={(e) => setImgWidth(e.target.value)}
      disabled={uploading}
      className="w-full px-2 py-1 border border-gray-300 text-sm font-mono"
     />
    </div>
   </div>
   {variants.filter(v => !v.isCustom).length > 0 && (
    <div>
     <p className="text-xs text-gray-600 mb-1">추천 너비 (활성 디스플레이 기준, 회전 반영):</p>
     <div className="flex flex-wrap gap-1">
      {/* ⚠️ 여기 너비는 **시청자가 보는 폭**이다. 세로 설치 화면은 패널이 3840x2160 이어도
          콘텐츠가 2160 폭이므로, 패널 폭(3840)을 그대로 쓰면 이미지→영상이 옆으로 눕는다.
          이 값은 image_width(생성될 영상의 실제 너비)로 그대로 전송된다. */}
      {Array.from(new Set(variants.filter(v => !v.isCustom).map(v => viewerDims(v).w)))
       .sort((a, b) => b - a)
       .map(w => {
        const usedBy = variants.filter(v => !v.isCustom && viewerDims(v).w === w);
        const totalClients = usedBy.reduce((sum, v) => sum + (v.clientCount || 0), 0);
        const isSelected = String(w) === imgWidth;
        return (
         <button
          key={w}
          type="button"
          onClick={() => setImgWidth(String(w))}
          disabled={uploading}
          className={`px-2 py-1 text-xs font-mono border ${
           isSelected
            ? 'bg-blue-600 text-white border-blue-700'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
          }`}
          title={usedBy.map(v => {
           const d = viewerDims(v);
           const rot = (v.rotation === 90 || v.rotation === 270) ? ` (패널 ${v.width}x${v.height} ⟳${v.rotation}°)` : '';
           return `${d.w}x${d.h}${rot}`;
          }).join(', ') + ` 디스플레이 ${totalClients}대`}
         >
          {w} ({totalClients})
         </button>
        );
       })}
     </div>
    </div>
   )}
  </div>
 )}
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">제목</label>
 <input
 type="text"
 name="title"
 required
 disabled={uploading}
 value={title}
 onChange={(e) => setTitle(e.target.value)}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
 <textarea
 name="description"
 rows={3}
 disabled={uploading}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">행사 시작 시각 <span className="text-xs font-normal text-gray-500">(선택 — 재생 시 남은 시간 오버레이 표시)</span></label>
 {/* flex + flex-1 min-w-0: datetime-local 네이티브 고유폭을 확실히 제어(비로그인 업로드와 동일). */}
 <div className="flex">
 <input
 type="datetime-local"
 name="event_start_at"
 value={eventStart}
 onChange={(e) => onChangeEventStart(e.target.value)}
 onBlur={onBlurEventStart}
 disabled={uploading}
 className="flex-1 min-w-0 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 </div>
 {/* 행사 시간 — **저장되지 않는 입력 보조**. 시작·종료와 양방향으로 물린다
     (utils/eventDuration — 영상 상세 모달·비로그인 업로드와 같은 규칙). */}
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">
  행사 시간 <span className="text-xs font-normal text-gray-500">(선택 — 넣으면 만료 시각이 계산됩니다)</span>
 </label>
 <div className="flex items-center gap-2">
  {/* step=1 — step=5 는 min=1 이 기준점이라 60·120 같은 5의 배수를 stepMismatch 로 거절했다 */}
  <input
   type="number"
   min="1"
   step="1"
   inputMode="numeric"
   value={durationMin}
   onChange={(e) => onChangeDuration(e.target.value)}
   disabled={uploading || !eventStart}
   title={eventStart ? '' : '행사 시작 시각을 먼저 입력하세요'}
   placeholder="60"
   className="w-24 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
  />
  <span className="text-sm text-gray-500">분</span>
  {humanizeMinutes(Number(durationMin)) && (
   <span className="text-xs text-gray-400">({humanizeMinutes(Number(durationMin))})</span>
  )}
 </div>
 </div>
 <div className="mb-4">
 <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
 <input
 type="checkbox"
 checked={expiresEnabled}
 onChange={(e) => {
  setExpiresEnabled(e.target.checked);
  if (e.target.checked && !eventExpires) {
   setEventExpires(localInputPlusHours(eventStart, 1));
   setDurationMin(String(DEFAULT_DURATION_MIN));
  }
 }}
 disabled={uploading}
 />
 만료 시각 <span className="text-xs font-normal text-gray-500">(= 행사 종료. 행사 시작·시간에서 계산됨)</span>
 </label>
 {expiresEnabled && (
 <div className="flex">
 <input
 type="datetime-local"
 value={eventExpires}
 onChange={(e) => onChangeExpires(e.target.value)}
 disabled={uploading}
 className="flex-1 min-w-0 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 )}
 {durationInvalid && (
  <p className="text-xs text-red-500 mt-1">만료가 행사 시작보다 이릅니다 — 시각을 확인해 주세요.</p>
 )}
 </div>
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">동적 라벨 <span className="text-xs font-normal text-gray-500">(선택 — 장소 등 짧은 정보, 재생 시 오버레이 표시)</span></label>
 <input
 type="text"
 name="event_label"
 value={eventLabel}
 onChange={(e) => setEventLabel(e.target.value)}
 maxLength={60}
 placeholder="예: 강남역 광장"
 disabled={uploading}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 <div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-2">
    화면 해상도별 Variant 만들기
    <span className="ml-2 text-xs font-normal text-gray-500">
      (현재 연결된 모든 디스플레이 해상도가 자동 추천됩니다)
    </span>
  </label>
  {variants.length === 0 ? (
    <p className="text-xs text-gray-500 mb-2">현재 연결된 활성 디스플레이 없음 — 아래에서 직접 추가 가능</p>
  ) : (
    <ul className="border border-gray-200 divide-y mb-2">
      {/*
        한 항목이 좁은 화면에서 한 줄에 안 들어간다(해상도·회전·대수·강제 fit·제거).
        flex-wrap 으로 **옵션 묶음을 통째로 아랫줄에 내린다** — 개별 요소가 제각각 접히면
        어느 항목의 옵션인지 읽기 어려워진다. sm 이상에선 종전대로 한 줄.
      */}
      {variants.map((v, i) => (
        <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-2 text-sm">
          {/* 체크박스+해상도를 한 label 로 묶어 텍스트로도 토글되게 — 모바일에서 체크박스만
              노리는 건 표적이 너무 작다. */}
          <label className="flex items-center gap-2 min-w-0 cursor-pointer">
            <input
              type="checkbox"
              className="shrink-0"
              checked={v.selected}
              disabled={uploading}
              onChange={(e) => setVariants(prev => prev.map((x, idx) => idx === i ? { ...x, selected: e.target.checked } : x))}
            />
            {/* 표기는 variant 정체성인 패널 치수 그대로. 세로 설치면 시청자 기준 치수를 덧붙여
                "3840×2160 인데 왜 세로 화면이지?" 하는 혼동을 없앤다(값은 건드리지 않는다). */}
            <span className="font-mono">{v.width}×{v.height}{v.fps !== null ? `@${v.fps.toFixed(2).replace(/\.?0+$/, '')}` : ''}</span>
          </label>
          {(v.rotation === 90 || v.rotation === 270) && (
            <span className="text-xs text-purple-600" title={`세로 설치 — 시청자 기준 ${viewerDims(v).w}×${viewerDims(v).h}`}>
              ⟳{v.rotation}° 세로 {viewerDims(v).w}×{viewerDims(v).h}
            </span>
          )}
          {v.clientCount !== undefined && v.clientCount > 0 && (
            <span className="text-xs text-gray-500" title={(v.clientNames || []).join(', ')}>
              ({v.clientCount}대)
            </span>
          )}
          {/* 옵션 묶음 — 모바일은 전폭(아랫줄), sm 이상은 오른쪽 정렬. 들여쓰기로 위 항목에 붙인다. */}
          <div className="w-full pl-6 sm:w-auto sm:pl-0 sm:ml-auto flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                className="shrink-0"
                checked={v.stretch}
                disabled={uploading}
                onChange={(e) => setVariants(prev => prev.map((x, idx) => idx === i ? { ...x, stretch: e.target.checked } : x))}
              />
              강제 fit
            </label>
            {v.isCustom && (
              <button
                type="button"
                disabled={uploading}
                onClick={() => setVariants(prev => prev.filter((_, idx) => idx !== i))}
                className="text-xs text-red-600 hover:underline"
              >
                제거
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  )}
  <div className="flex flex-wrap items-center gap-1 gap-y-2 text-xs">
    <input
      type="number"
      placeholder="W"
      value={customW}
      disabled={uploading}
      onChange={e => setCustomW(e.target.value)}
      className="w-16 px-2 py-1 border border-gray-300"
    />
    <span>×</span>
    <input
      type="number"
      placeholder="H"
      value={customH}
      disabled={uploading}
      onChange={e => setCustomH(e.target.value)}
      className="w-16 px-2 py-1 border border-gray-300"
    />
    <span>@</span>
    <input
      type="number"
      step="0.001"
      placeholder="fps"
      value={customFps}
      disabled={uploading}
      onChange={e => setCustomFps(e.target.value)}
      className="w-20 px-2 py-1 border border-gray-300"
    />
    <label className="flex items-center gap-1 ml-2">
      <input type="checkbox" checked={customStretch} disabled={uploading} onChange={e => setCustomStretch(e.target.checked)} />
      강제 fit
    </label>
    <button
      type="button"
      disabled={uploading}
      onClick={addCustomVariant}
      className="ml-2 px-2 py-1 bg-gray-200 hover:bg-gray-300"
    >
      추가
    </button>
  </div>
  <p className="text-xs text-gray-500 mt-1">
    체크된 항목 각각이 별도 인코딩 작업으로 등록됩니다. 강제 fit은 원본 비율을 무시하고 W×H에 끼워 맞춥니다 (letterbox 없음).
  </p>
 </div>
 <div className="flex justify-end gap-3">
 <button
 type="button"
 onClick={() => {
 setTitle('');
 onClose();
 }}
 disabled={uploading}
 className="px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
 >
 취소
 </button>
 <button
 type="submit"
 disabled={uploading || !!fileError || durationInvalid}
 className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
 >
 {uploading && (
 <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
 </svg>
 )}
 {uploading ? (progress < 100 ? `올리는 중 ${Math.round(progress)}%` : '올리기 끝 · 확인 중') : '업로드'}
 </button>
 </div>
 </form>
 {uploading && (
 <div className="mt-4">
 {/* 회전 스피너 — 바이트 업로드가 100% 된 뒤 서버 처리 대기 동안에도 계속 움직여
     "멈춤"으로 오인하지 않게 한다. progress<100=업로드, 100=서버 처리(썸네일, 이미지는
     영상 변환) 단계. 재생용 인코딩은 응답 뒤 백그라운드 — 목록 카드의 "변환 중 N%"가 그것. */}
 <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
 <svg className="w-4 h-4 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
 </svg>
 {progress < 100
  ? `올리는 중... ${Math.round(progress)}%`
  : '올리기 끝 · 확인하고 있습니다 (몇 초 걸립니다). 게재 준비는 목록 카드에서 이어서 보입니다.'}
 </div>
 <div className="w-full bg-gray-200 h-2 overflow-hidden">
 {progress < 100 ? (
 <div className="bg-blue-600 h-2 transition-all duration-300" style={{ width: `${progress}%` }} />
 ) : (
 // 변환 단계 — 진행률을 알 수 없어 무한 애니메이션으로 움직임 유지.
 <div className="bg-blue-600 h-2 animate-pulse w-full" />
 )}
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

// Main MoFile Page Component
const MoFilePage: React.FC = () => {
 const { files, isLoading, error, currentFilter, setFiles, setLoading, setError, setFilter, deleteFile, updateFileTitle } = useMoFileStore();
 const [searchTerm, setSearchTerm] = useState('');
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [showAiModal, setShowAiModal] = useState(false);
 const [aiEnabled, setAiEnabled] = useState(false);
 const [selectedFile, setSelectedFile] = useState<MoFile | null>(null);
 // 행사 현황 달력(비로그인 업로드와 동일 컴포넌트). 접기/펼치기 토글.
 const [calendarEvents, setCalendarEvents] = useState<CalEvent[]>([]);
 const [showCalendar, setShowCalendar] = useState(true);
 // 예정된 행사인데 슬롯셋 어디에도 안 들어간 영상 — 그날 재생되지 않는다. 서버가 판정한다
 // (직접배정·박스→슬롯 결합·박스 기본영상 3경로). 행사일은 지나면 되돌릴 수 없어 경고가 필요하다.
 const [unscheduled, setUnscheduled] = useState<Array<{ file_hash: string; title: string; event_start_at: string }>>([]);
 // 영상 선택(다중) + 선택 삭제.
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkDeleting, setBulkDeleting] = useState(false);

 // 달력 칸 클릭 → 그 날 영상의 상세 모달. 목록에 이미 있으면 그 객체를 그대로 쓴다
 // (목록에서 클릭한 것과 동일한 상태 — 편집·삭제가 store 와 일관되게 반영된다).
 // 달력에는 **내 업로드 목록에 없는 파일**도 뜬다(승인된 익명 업로드 등 박스 항목).
 // 그 경우에만 /info 로 보충한다 — 목록 우선이라 평상시엔 추가 요청이 없다.
 // 하루에 여러 건이면 첫 번째를 연다(칸이 작아 선택 UI 를 넣지 않았다 — 툴팁이 건수를 알린다).
 const openEventFile = async (events: CalEvent[]) => {
  const ev = events[0];
  if (!ev) return;
  const known = files.find(f => f.file_hash === ev.fileHash);
  if (known) { setSelectedFile(known); return; }
  try {
   const r = await fetch(`/mofile/${ev.fileHash}/info`, { credentials: 'include' });
   const body = r.ok ? await r.json() : null;
   if (!body?.success || !body.data) {
    alert('이 영상의 정보를 불러오지 못했습니다.');
    return;
   }
   const d = body.data;
   setSelectedFile({
    file_hash: d.file_hash,
    title: d.title ?? ev.filename ?? '',
    file_type: d.file_type ?? undefined,
    duration: d.duration ?? null,
    thumbnail_path: d.thumbnail_path ?? ev.thumbnailPath ?? undefined,
    event_start_at: d.event_start_at ?? ev.eventStartAt,
    expires_at: d.expires_at ?? null,
    publish_at: d.publish_at ?? null,
    // /info 는 업로드 일자를 주지 않는다 — 행사 시작으로 대체하지 않고 비워 둔다
    // (없는 정보를 그럴듯한 값으로 채우면 화면이 거짓말을 한다).
    created_at: '',
    file_url: `/mofile/download/${d.file_hash}`,
   });
  } catch {
   alert('이 영상의 정보를 불러오지 못했습니다.');
  }
 };

 const toggleSelect = (hash: string) => setSelectedIds(prev => {
  const next = new Set(prev);
  if (next.has(hash)) next.delete(hash); else next.add(hash);
  return next;
 });

 // 현재 목록에서 만료된 파일만 선택.
 const selectExpired = () => setSelectedIds(new Set(files.filter(f => isExpired(f.expires_at)).map(f => f.file_hash)));

 // 선택 삭제 — 각 파일 DELETE /mofile/:hash 병렬. 성공분만 로컬 제거, 실패는 알림.
 const handleBulkDelete = async () => {
  if (selectedIds.size === 0) return;
  if (!confirm(`선택한 ${selectedIds.size}개 파일을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
  setBulkDeleting(true);
  const hashes = Array.from(selectedIds);
  const results = await Promise.allSettled(
   hashes.map(h => fetch(`/mofile/${h}`, { method: 'DELETE', credentials: 'include' }))
  );
  const failed: string[] = [];
  results.forEach((r, i) => {
   if (r.status === 'fulfilled' && r.value.ok) deleteFile(hashes[i]);
   else failed.push(hashes[i]);
  });
  setSelectedIds(new Set());
  setBulkDeleting(false);
  if (failed.length) alert(`${failed.length}개 삭제에 실패했습니다.`);
 };

 // useRef for stable references
 const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
 const isMountedRef = useRef(false);

 // AI 영상 생성 기능 활성 여부 — 버튼 노출 결정
 useEffect(() => {
  fetch('/aivideo/config', { credentials: 'include' })
   .then(r => r.json())
   .then(j => { if (j.success) setAiEnabled(!!j.data?.enabled); })
   .catch(() => {});
 }, []);

 // 행사 현황 달력 데이터 로드(내가 소유한 박스의 이번 달·다음 달 행사일). 실패는 무시(부가 정보).
 useEffect(() => {
  fetch('/mofile/calendar', { credentials: 'include' })
   .then(r => r.json())
   .then(j => { if (j.success) setCalendarEvents(j.data?.events ?? []); })
   .catch(() => {});
 }, []);

 // ── 퀵UI 편성 문진 (달력 드롭 진입) ──────────────────────────────────
 // 드롭 → 이전 레시피(GET /mofile/:hash/recipe) 프리필 → 문진 모달 → 제출 후
 // 파일 목록·달력 refetch (영수증은 모달이 표시).
 const [interview, setInterview] = useState<{
  fileHash: string; title: string; dateKey: string;
  prefill: { eventStartAt?: string | null; promoStartAt?: string | null; promoEndAt?: string | null; placement?: 'box' | 'slot' | 'none' | null; isPublic?: boolean } | null;
 } | null>(null);

 const openRecipeInterview = async (dateKey: string, payload: { fileHash: string; title: string }) => {
  let prefill = null;
  try {
   const r = await fetch(`/mofile/${payload.fileHash}/recipe`, { credentials: 'include' });
   const j = await r.json();
   if (j?.success && j.data) {
    prefill = {
     eventStartAt: j.data.eventStartAt, promoStartAt: j.data.promoStartAt,
     promoEndAt: j.data.promoEndAt, placement: j.data.placement, isPublic: j.data.isPublic,
    };
   }
  } catch { /* 프리필 실패는 무시 — 기본값 문진 */ }
  setInterview({ fileHash: payload.fileHash, title: payload.title, dateKey, prefill });
 };

 const submitRecipe = async (values: RecipeValues) => {
  if (!interview) return { ok: false, error: '대상 없음' };
  const r = await fetch(`/mofile/${interview.fileHash}/recipe`, {
   method: 'POST', credentials: 'include',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify(toRecipeBody(values)),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j?.success) return { ok: false, error: j?.error };
  // 편성 결과 반영 — 목록(만료/행사 배지)·달력 갱신.
  loadFiles();
  fetch('/mofile/calendar', { credentials: 'include' })
   .then(res => res.json())
   .then(cj => { if (cj.success) setCalendarEvents(cj.data?.events ?? []); })
   .catch(() => {});
  return { ok: true, receipt: j.data };
 };

 // 미배치 경고 데이터. files 가 바뀌면(업로드·삭제·슬롯 편집 후 재로드) 다시 판정한다.
 useEffect(() => {
  fetch('/mofile/unscheduled', { credentials: 'include' })
   .then(r => r.json())
   .then(j => { if (j.success) setUnscheduled(j.data?.files ?? []); })
   .catch(() => {});
 }, [files]);

 // Load files function - no dependencies to avoid recreating
 const loadFiles = async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams();
 if (currentFilter !== 'all') {
 params.append('type', currentFilter);
 }
 if (searchTerm) {
 params.append('search', searchTerm);
 }

 const response = await fetch(`/mofile?${params}`, {
 credentials: 'include'
 });

 if (response.ok) {
 const data = await response.json();
 setFiles(data.data || []);
 } else {
 throw new Error('파일 목록을 불러오는데 실패했습니다.');
 }
 } catch (error) {
 console.error('Error loading files:', error);
 setError('파일을 불러오는 중 오류가 발생했습니다.');
 } finally {
 setLoading(false);
 }
 };

 // Initial load
 useEffect(() => {
 loadFiles();
 isMountedRef.current = true;
 }, []); // Empty array - only run once

 // Handle filter and search changes
 useEffect(() => {
 // Skip on initial mount
 if (!isMountedRef.current) return;

 // 필터/검색 바뀌면 선택 초기화 — 화면에서 사라진 항목이 선택된 채 삭제되는 사고 방지.
 setSelectedIds(new Set());

 // Clear any existing timeout
 if (searchTimeoutRef.current) {
 clearTimeout(searchTimeoutRef.current);
 }

 // Set new timeout for debounced load
 searchTimeoutRef.current = setTimeout(() => {
 loadFiles();
 }, 300);

 // Cleanup
 return () => {
 if (searchTimeoutRef.current) {
 clearTimeout(searchTimeoutRef.current);
 }
 };
 }, [currentFilter, searchTerm]);

 // Global access for legacy code
 useEffect(() => {
 (window as any).MoFile = {
 viewFile: (fileHash: string) => {
 const file = files.find(f => f.file_hash === fileHash);
 if (file) setSelectedFile(file);
 }
 };

 return () => {
 delete (window as any).MoFile;
 };
 }, [files]);

 // ── 변환 진행 중 파일 — Basic.tsx 와 같은 방식(주기 폴링 대신 WS 푸시 + 새로 고침 버튼).
 // 종전엔 5초마다 목록을 다시 읽었다(폴링 금지 규약 위반 + loading 깜빡임). 지금은
 // transcode_complete 푸시가 오면 그때 1회 읽고, 창 복귀 때도 읽는다. 푸시를 놓친 경우는
 // 안내 띠의 새로 고침 버튼(목록 재조회 + 스토어 화해)으로 수습한다.
 // 목록(DB) 값을 스토어에 seed(newest-wins) — 새로고침 뒤에도 %·실패 사유가 남고, WS 보다 오래된 값이 덮지 않는다.
 useEffect(() => {
  const seed = useFileTranscodeStore.getState().seed;
  for (const f of files) {
   const st = f.transcode_status;
   if (!f.file_type?.startsWith('video/') || !st || st === 'done') continue;
   seed(f.file_hash, null, {
    status: st, progress: f.transcode_progress ?? 0,
    preparing: st === 'processing' && !(f as any).encoding_started_at,
    error: f.transcode_error ?? undefined,
   });
  }
 }, [files]);
 const pendingHashes = files
  .filter(f => f.file_type?.startsWith('video/') && (f.transcode_status === 'pending' || f.transcode_status === 'processing'))
  .map(f => f.file_hash);
 const transcodeEntries = useFileTranscodeStore(s => s.entries);
 const refreshedForDone = useRef<Set<string>>(new Set());
 useEffect(() => {
  const done = pendingHashes.filter(h => {
   const e = transcodeEntries.get(`${h}::main`);
   return (e?.status === 'done' || e?.status === 'failed') && !refreshedForDone.current.has(h);
  });
  if (done.length === 0) return;
  for (const h of done) refreshedForDone.current.add(h);
  void loadFiles();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [transcodeEntries, pendingHashes.join(',')]);
 useEffect(() => {
  if (pendingHashes.length === 0) return;
  const onFocus = () => { void loadFiles(); };
  window.addEventListener('focus', onFocus);
  return () => window.removeEventListener('focus', onFocus);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [pendingHashes.join(',')]);
 const [refreshingTranscode, setRefreshingTranscode] = useState(false);
 const refreshTranscode = async () => {
  setRefreshingTranscode(true);
  try {
   await Promise.all([loadFiles(), useFileTranscodeStore.getState().reconcileStale()]);
  } finally { setRefreshingTranscode(false); }
 };

 return (
 <div>
 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-4">
 <h1 className="text-2xl md:text-3xl font-bold text-gray-800">영상</h1>
 <div className="flex gap-2">
 {aiEnabled && (
 <button
 className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 transition-colors"
 onClick={() => setShowAiModal(true)}
 title="이미지 한 장 + 프롬프트로 시네마그래프 영상을 생성합니다"
 >
 🎬 AI 영상 생성
 </button>
 )}
 <button
 className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 transition-colors"
 onClick={() => setShowUploadModal(true)}
 >
 영상·이미지 업로드
 </button>
 </div>
 </div>
 <p className="text-sm text-gray-500 leading-relaxed mb-6">
 스크린에서 재생할 영상을 업로드합니다. 모패밀리의 슬롯에 배치하면 송출이 시작됩니다.
 </p>

 {/* 변환 진행 안내 — 변환이 안 끝난 영상이 있는 동안만. 완료 푸시를 받으면 자동으로
     사라지고, 푸시를 놓쳤을 때를 위해 새로 고침 버튼을 같이 둔다(Basic.tsx 와 동일). */}
 {pendingHashes.length > 0 && (
 <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-sm flex items-center gap-3 flex-wrap">
  <svg className="w-4 h-4 shrink-0 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
  </svg>
  <span className="flex-1 min-w-[12rem] text-blue-900">
   영상 {pendingHashes.length}개를 게재 준비하고 있습니다. 끝나면 카드의 "게재 준비 중" 표시가 자동으로 사라집니다.
   한참 지나도 그대로면 새로 고침해 주세요.
  </span>
  <button
   type="button"
   onClick={() => void refreshTranscode()}
   disabled={refreshingTranscode}
   className="shrink-0 px-2.5 py-1 text-xs font-semibold border bg-white text-blue-700 border-blue-300 hover:bg-blue-100 disabled:opacity-50"
  >{refreshingTranscode ? '확인 중…' : '새로 고침'}</button>
 </div>
 )}

 {/* 미배치 경고 — 예정 행사인데 슬롯셋에 못 들어간 영상. 달력보다 위에 둔다(놓치면 행사가 지나간다). */}
 {unscheduled.length > 0 && (
 <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
  <p className="text-sm font-semibold text-amber-900 mb-1">
   ⚠️ 슬롯에 넣지 않아 재생되지 않는 예정 영상 {unscheduled.length}건
  </p>
  <p className="text-xs text-amber-800 mb-2">
   행사 시작 시각만 넣어서는 재생되지 않습니다. 슬롯에 직접 넣거나, 슬롯에 연결된 영상 박스에 넣어야 합니다.
  </p>
  <ul className="text-sm space-y-1">
   {unscheduled.map(u => (
    <li key={u.file_hash}>
     <button
      type="button"
      onClick={() => { const f = files.find(x => x.file_hash === u.file_hash); if (f) setSelectedFile(f); }}
      className="text-amber-900 underline hover:text-amber-700"
     >
      {u.title || '(제목 없음)'}
     </button>
     <span className="text-amber-700 ml-2">{new Date(u.event_start_at).toLocaleString('ko-KR')}</span>
    </li>
   ))}
  </ul>
 </div>
 )}

 {/* 행사 현황 달력 — 비로그인 업로드와 동일 컴포넌트. 내가 소유한 박스의 이번/다음 달 행사일 오버뷰. */}
 <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 relative">
 <button
 type="button"
 onClick={() => setShowCalendar(v => !v)}
 className="absolute top-3 right-4 text-xs text-gray-400 hover:text-gray-600"
 >
 {showCalendar ? '접기 ▲' : '펼치기 ▼'}
 </button>
 {showCalendar
 ? <UploadCalendar events={calendarEvents} twoColumn onSelectEvents={openEventFile} onDropVideo={openRecipeInterview} />
 : <h3 className="text-sm font-semibold text-gray-700">행사 현황 달력</h3>}
 </div>

 {interview && (
 <RecipeInterview
 title={interview.title}
 dropDateKey={interview.dateKey}
 prefill={interview.prefill}
 onSubmit={submitRecipe}
 onClose={() => setInterview(null)}
 />
 )}

 {/* 필터 및 검색 */}
 <div className="flex flex-col sm:flex-row gap-4 mb-6">
 <div className="flex gap-2">
 {(['all', 'video', 'image'] as const).map((filter) => (
 <button
 key={filter}
 className={`px-3 py-1 transition-colors ${
 currentFilter === filter
 ? 'bg-blue-500 text-white'
 : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
 }`}
 onClick={() => setFilter(filter)}
 >
 {filter === 'all' ? '전체' : filter === 'video' ? '동영상' : '이미지'}
 </button>
 ))}
 {(() => {
 const expiredCount = files.filter(f => isExpired(f.expires_at)).length;
 return (
 <button
 type="button"
 onClick={selectExpired}
 disabled={expiredCount === 0}
 className="px-3 py-1 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 disabled:opacity-40 whitespace-nowrap"
 title="현재 목록의 만료된 파일을 모두 선택합니다"
 >
 만료 파일 선택{expiredCount > 0 ? ` (${expiredCount})` : ''}
 </button>
 );
 })()}
 </div>
 <div className="flex-1">
 <input
 type="text"
 placeholder="파일명 검색"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 </div>

 {/* 선택 툴바 — 하나 이상 선택 시 표시 */}
 {selectedIds.size > 0 && (
 <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
 <span className="text-sm font-medium text-blue-800">{selectedIds.size}개 선택됨</span>
 <button
 onClick={handleBulkDelete}
 disabled={bulkDeleting}
 className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-sm rounded disabled:opacity-50"
 >
 {bulkDeleting ? '삭제 중...' : '선택 삭제'}
 </button>
 <button onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-600 hover:text-gray-800">
 선택 해제
 </button>
 </div>
 )}

 {/* 파일 목록 */}
 {error && (
 <div className="text-center py-8 text-red-500">
 {error}
 </div>
 )}

 {isLoading ? (
 <div className="text-center py-8 text-gray-500">로딩 중...</div>
 ) : files.length === 0 ? (
 <div className="text-center py-8 text-gray-500">
 아직 업로드된 파일이 없습니다.
 </div>
 ) : (
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
 {files.map((file) => (
 <FileCard
 key={file.file_hash}
 file={file}
 onClick={() => setSelectedFile(file)}
 selected={selectedIds.has(file.file_hash)}
 onToggleSelect={() => toggleSelect(file.file_hash)}
 />
 ))}
 </div>
 )}

 {/* Modals */}
 <UploadModal
 isOpen={showUploadModal}
 onClose={() => setShowUploadModal(false)}
 onSuccess={() => loadFiles()}
 />

 <AiVideoModal
 isOpen={showAiModal}
 onClose={() => setShowAiModal(false)}
 onSuccess={() => loadFiles()}
 />

 <FileDetailModal
 file={selectedFile}
 onClose={() => setSelectedFile(null)}
 onDelete={() => {
 if (selectedFile) {
 deleteFile(selectedFile.file_hash);
 }
 }}
 onUpdate={(fileHash, newTitle) => {
 updateFileTitle(fileHash, newTitle);
 setSelectedFile(prev => prev ? { ...prev, title: newTitle } : null);
 }}
 />
 </div>
 );
};

export default MoFilePage;
